import React, { useCallback, useEffect, useState } from "react";
import Modal from "react-modal";
import { Button } from "../Button";
import { formatCurrency, formatNumber } from "../../helpers/formatNumber";
import { CashbackRequestsAdminService } from "../../api/generated/services/CashbackRequestsAdminService";
import { CashbackRequestViewModel } from "../../api/generated/models/CashbackRequestViewModel";
import { CashbackRequestReviewContextViewModel } from "../../api/generated/models/CashbackRequestReviewContextViewModel";
import { CashbackSource } from "../../api/generated/models/CashbackSource";
import { ReactComponent as CloseIcon } from "../../assets/icons/x.svg";
import "../../api/openApiConfig";

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
    <div className="break-all text-sm mt-0.5">{children}</div>
  </div>
);

/** Every block in the modal gets the same frame, so the sections read as separate things. */
const Section: React.FC<{
  title: string;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, children }) => (
  <section className="border border-gray-200 rounded-xl">
    <header className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-xl">
      <h3 className="text-sm font-medium text-gray-900">{title}</h3>
      {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
    </header>
    <div className="p-4">{children}</div>
  </section>
);

const STATE_BADGES: Record<string, string> = {
  Pending: "bg-amber-100 text-amber-800",
  Accepted: "bg-green-100 text-green-800",
  Declined: "bg-red-100 text-red-800",
};

const TH = "py-2 pr-4 font-medium whitespace-nowrap";
const TD = "py-2 pr-4 align-top";

const orDash = (value?: string | number | boolean | null) =>
  value === null || value === undefined || value === "" ? "-" : String(value);

/** Admins type Danish amounts, where the comma is the decimal separator. */
const parseAmount = (value: string): number | null => {
  const normalised = value.trim().replace(",", ".");
  if (normalised === "") return null;
  const parsed = Number(normalised);
  return Number.isFinite(parsed) ? parsed : null;
};

const SOURCE_LABELS: Record<string, string> = {
  [CashbackSource.CARD_LINKING_BENEFIT]: "Calculated from the benefit percentages",
  [CashbackSource.PARSED_DISPLAY]: "Calculated from the benefit's cashback display",
  [CashbackSource.ADMIN_OVERRIDE]: "Entered by an admin",
  [CashbackSource.UNAVAILABLE]: "Could not be calculated — enter the amounts below",
};

interface ClaimConfig {
  label: string;
  send: (requestBody: { cashbackRequestId?: string; orderId?: string }) => Promise<unknown>;
}

const CLAIM_CONFIGS: Record<string, ClaimConfig> = {
  AdTraction: {
    label: "AdTraction",
    send: (requestBody) =>
      CashbackRequestsAdminService.postApiCashbackRequestsAdminCreateAdtractionClaim(requestBody),
  },
  Impact: {
    label: "Impact",
    send: (requestBody) =>
      CashbackRequestsAdminService.postApiCashbackRequestsAdminCreateImpactClaim(requestBody),
  },
};

type PendingAction = "accept" | "decline" | null;

interface CashbackRequestDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: CashbackRequestViewModel | null;
  onRequestChanged: () => void;
}

const CashbackRequestDetailsModal: React.FC<CashbackRequestDetailsModalProps> = ({
  isOpen,
  onClose,
  request,
  onRequestChanged,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [orderId, setOrderId] = useState("");
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [claimMessage, setClaimMessage] = useState<string | null>(null);
  const [totalCashback, setTotalCashback] = useState("");
  const [customerCashback, setCustomerCashback] = useState("");
  const [context, setContext] = useState<CashbackRequestReviewContextViewModel | null>(null);
  const [contextError, setContextError] = useState<string | null>(null);

  const requestId = request?.id;

  useEffect(() => {
    setTotalCashback(String(request?.totalAssignedCashbackDecimal ?? 0).replace(".", ","));
    setCustomerCashback(String(request?.customerAssignedCashbackDecimal ?? 0).replace(".", ","));
  }, [request?.totalAssignedCashbackDecimal, request?.customerAssignedCashbackDecimal]);

  useEffect(() => {
    if (!isOpen || !requestId) return;
    let cancelled = false;

    (async () => {
      try {
        setContextError(null);
        const response =
          await CashbackRequestsAdminService.postApiCashbackRequestsAdminReviewContext({
            cashbackRequestId: requestId,
          });
        if (!cancelled) setContext(response);
      } catch (err) {
        if (!cancelled) setContextError("Could not load benefits and customer history");
        console.error("Error loading cashback request review context:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, requestId]);

  const validateAmounts = useCallback((): string | null => {
    const total = parseAmount(totalCashback);
    const customer = parseAmount(customerCashback);

    if (total === null || customer === null) return "Both cashback amounts must be numbers";
    if (total < 0 || customer < 0) return "Cashback amounts cannot be negative";
    if (total === 0 && customer === 0) {
      return "Accepting with zero cashback would credit the customer nothing";
    }
    if (customer > total) return "Customer cashback cannot exceed total cashback";
    if (request?.totalAmountDecimal !== undefined && total > request.totalAmountDecimal) {
      return "Total cashback cannot exceed the purchase amount";
    }
    return null;
  }, [totalCashback, customerCashback, request?.totalAmountDecimal]);

  if (!request) {
    return null;
  }

  const isPending = request.state === "Pending";
  const claimConfig = request.affiliateTransactionType
    ? CLAIM_CONFIGS[request.affiliateTransactionType]
    : undefined;
  const amountError = validateAmounts();

  const runAction = async () => {
    if (!pendingAction) return;
    try {
      setSubmitting(true);
      setError(null);
      if (pendingAction === "accept") {
        await CashbackRequestsAdminService.postApiCashbackRequestsAdminAccept({
          cashbackRequestId: request.id,
          totalCashback: parseAmount(totalCashback) ?? undefined,
          customerCashback: parseAmount(customerCashback) ?? undefined,
        });
      } else {
        await CashbackRequestsAdminService.postApiCashbackRequestsAdminDecline({
          cashbackRequestId: request.id,
        });
      }
      onRequestChanged();
      onClose();
    } catch (err) {
      setError(pendingAction === "accept" ? "Failed to accept request" : "Failed to decline request");
      console.error("Error updating cashback request:", err);
    } finally {
      setSubmitting(false);
      setPendingAction(null);
    }
  };

  const submitClaim = async () => {
    if (!claimConfig || !orderId.trim()) return;
    try {
      setClaimSubmitting(true);
      setClaimMessage(null);
      await claimConfig.send({
        cashbackRequestId: request.id,
        orderId: orderId.trim(),
      });
      setClaimMessage(`Claim sent to ${claimConfig.label}`);
      onRequestChanged();
    } catch (err) {
      setClaimMessage(`Failed to send claim to ${claimConfig.label}`);
      console.error(`Error creating ${claimConfig.label} claim:`, err);
    } finally {
      setClaimSubmitting(false);
    }
  };

  const benefits = context?.benefits ?? [];
  const obsNotes = context?.business?.obsNotes ?? [];
  const history = context?.customerHistory;
  const transactionWindow = context?.transactionWindow;
  const windowTransactions = transactionWindow?.transactions ?? [];

  // A purchase already credited for this business, for an amount matching the claim, is the one
  // finding that settles the request on its own: it has been paid for once already.
  const probableDuplicate = windowTransactions.find(
    (transaction) => transaction.isCredited && transaction.isSameBusiness && transaction.isAmountClose
  );

  return (
    <Modal
      className="modal-content md:w-[95vw] md:max-w-[1280px] max-h-[90vh] p-0 flex flex-col"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Cashback Request Details"
    >
      <header className="flex justify-between items-center gap-4 px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <h2 className="text-primary font-medium text-xl">Cashback Request</h2>
          {request.state && (
            <span
              className={`text-xs px-2 py-1 rounded-full ${
                STATE_BADGES[request.state] ?? "bg-gray-100 text-gray-700"
              }`}
            >
              {request.state}
            </span>
          )}
        </div>
        <Button onClick={onClose} variant="icon">
          <CloseIcon className="w-3 h-3" />
        </Button>
      </header>

      {/* The only scrolling region: the header and the action bar stay put however long this gets. */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        <Section title="The claim">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Merchant">{orDash(request.merchantName)}</Field>
              <Field label="Purchase time">
                {request.purchaseTime
                  ? new Date(request.purchaseTime).toLocaleString("da-DK")
                  : "-"}
              </Field>
              <Field label="Amount claimed">{formatCurrency(request.totalAmount ?? 0)} DKK</Field>
              <Field label="Affiliate partner">{orDash(request.affiliateTransactionType)}</Field>
              <Field label="Order ID">{orDash(request.orderId)}</Field>
              <Field label="Claim sent">{request.claimCreatedAutomatically ? "YES" : "-"}</Field>
              <Field label="Request ID">
                <span className="font-mono text-xs">{request.id}</span>
              </Field>
              <Field label="Identity ID">
                <span className="font-mono text-xs">{orDash(request.identityId)}</span>
              </Field>
              {request.comment && (
                <div className="sm:col-span-2">
                  <Field label="Comment">
                    <span className="whitespace-pre-line">{request.comment}</span>
                  </Field>
                </div>
              )}
            </div>

            {/* The receipt is the primary evidence, so it sits beside the claim rather than at the bottom. */}
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Receipt</div>
              {request.confirmationUrl ? (
                <a href={request.confirmationUrl} target="_blank" rel="noreferrer">
                  <img
                    src={request.confirmationUrl}
                    alt="Purchase confirmation"
                    className="max-h-[240px] rounded-lg border border-gray-200"
                  />
                </a>
              ) : (
                <p className="text-sm text-gray-500">None attached.</p>
              )}
            </div>
          </div>
        </Section>

        {contextError && (
          <div className="text-sm bg-red-50 border border-red-200 text-red-900 p-3 rounded-lg">
            {contextError}
          </div>
        )}
        {!context && !contextError && (
          <p className="text-sm text-gray-500">Loading review context…</p>
        )}

        {transactionWindow && (
          <Section
            title="Transactions around the purchase date"
            subtitle={
              <>
                {transactionWindow.windowHours} hours either side
                {transactionWindow.fromUtc && transactionWindow.toUtc
                  ? ` — ${new Date(transactionWindow.fromUtc).toLocaleDateString(
                      "da-DK"
                    )} to ${new Date(transactionWindow.toUtc).toLocaleDateString("da-DK")}`
                  : ""}
              </>
            }
          >
            {probableDuplicate && (
              <div className="text-sm bg-red-50 border border-red-200 text-red-900 p-3 rounded-lg mb-4">
                This purchase may already have been credited:{" "}
                <strong>{orDash(probableDuplicate.merchantName)}</strong> for{" "}
                <strong>{formatNumber(probableDuplicate.purchaseAmount ?? 0)} DKK</strong> on{" "}
                {probableDuplicate.transactionTime
                  ? new Date(probableDuplicate.transactionTime).toLocaleString("da-DK")
                  : "-"}
                .
              </div>
            )}

            {windowTransactions.length === 0 ? (
              <p className="text-sm text-gray-500">No purchases or card activity in this window.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                      <th className={TH}>Time</th>
                      <th className={TH}>Merchant</th>
                      <th className={TH}>Amount</th>
                      <th className={TH}>Cashback</th>
                      <th className={TH}>Source</th>
                      <th className="py-2 font-medium whitespace-nowrap">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {windowTransactions.map((transaction, index) => (
                      <tr
                        key={`${transaction.transactionTime ?? "row"}-${index}`}
                        className={`border-b border-gray-100 last:border-0 ${
                          transaction.isSameBusiness
                            ? "bg-amber-50 font-medium"
                            : transaction.isAmountClose
                            ? "bg-gray-50"
                            : ""
                        }`}
                      >
                        <td className={`${TD} whitespace-nowrap`}>
                          {transaction.transactionTime
                            ? new Date(transaction.transactionTime).toLocaleString("da-DK")
                            : "-"}
                        </td>
                        <td className={TD}>
                          <div>{orDash(transaction.merchantName)}</div>
                          {transaction.merchantDetails && (
                            <div className="text-xs text-gray-500">
                              {transaction.merchantDetails}
                            </div>
                          )}
                        </td>
                        <td className={`${TD} whitespace-nowrap`}>
                          {transaction.purchaseAmount === null ||
                          transaction.purchaseAmount === undefined
                            ? "-"
                            : `${formatNumber(transaction.purchaseAmount)} DKK`}
                          {transaction.isAmountClose && (
                            <div className="text-xs text-amber-700">matches claim</div>
                          )}
                        </td>
                        <td className={`${TD} whitespace-nowrap`}>
                          {formatNumber(transaction.customerCashback ?? 0)} DKK
                        </td>
                        <td className={`${TD} whitespace-nowrap`}>{orDash(transaction.source)}</td>
                        <td className="py-2 align-top">
                          {transaction.isCredited ? (
                            "Credited"
                          ) : (
                            <span className="text-gray-500">
                              Not credited
                              {transaction.assignmentState
                                ? ` (${transaction.assignmentState})`
                                : ""}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        )}

        {context && (
          <Section
            title="Which rate applies"
            subtitle="Benefits in Contentful and the terms written on the business profile"
          >
            {history && (
              <p className="text-sm mb-4">
                {history.isFirstPurchase
                  ? "First purchase with this business."
                  : `Returning customer — ${history.priorTransactionCount} earlier purchase(s) with this business.`}
              </p>
            )}

            {(obsNotes.length > 0 || context.business?.additionalInstructions) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                {obsNotes.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                      Terms on the business profile
                    </div>
                    <ul className="list-disc list-inside text-sm space-y-1">
                      {obsNotes.map((note) => (
                        <li key={note}>{note}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {context.business?.additionalInstructions && (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                      Additional instructions
                    </div>
                    <p className="text-sm whitespace-pre-line">
                      {context.business.additionalInstructions}
                    </p>
                  </div>
                )}
              </div>
            )}

            {benefits.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                      <th className={TH}>Benefit</th>
                      <th className={TH}>Display</th>
                      <th className={TH}>Customer %</th>
                      <th className={TH}>Total %</th>
                      <th className={TH}>First purchase only</th>
                      <th className="py-2 font-medium whitespace-nowrap">Live at purchase</th>
                    </tr>
                  </thead>
                  <tbody>
                    {benefits.map((benefit, index) => (
                      <tr
                        key={`${benefit.name ?? "benefit"}-${index}`}
                        className={`border-b border-gray-100 last:border-0 ${
                          benefit.isActiveAtPurchaseTime ? "" : "text-gray-400"
                        }`}
                      >
                        <td className={TD}>{orDash(benefit.name)}</td>
                        <td className={TD}>{orDash(benefit.customerCashbackDisplay)}</td>
                        <td className={TD}>{orDash(benefit.customerCashbackValue)}</td>
                        <td className={TD}>{orDash(benefit.totalCashbackValue)}</td>
                        <td className={TD}>{benefit.isFirstTransactionBenefit ? "YES" : "-"}</td>
                        <td className="py-2 align-top">
                          {benefit.isActiveAtPurchaseTime ? "YES" : "no"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No benefits on this business.</p>
            )}
          </Section>
        )}

        <Section
          title="Cashback"
          subtitle={request.cashbackSource ? SOURCE_LABELS[request.cashbackSource] : undefined}
        >
          {request.cashbackNote && (
            <div className="text-sm bg-amber-50 border border-amber-200 text-amber-900 p-3 rounded-lg mb-4">
              {request.cashbackNote}
            </div>
          )}

          {isPending ? (
            <>
              <div className="flex flex-wrap items-end gap-4">
                <label className="block">
                  <span className="text-xs uppercase tracking-wide text-gray-500">
                    Total cashback (DKK)
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="mt-1 block w-[180px] p-2 border border-gray-300 rounded-md"
                    value={totalCashback}
                    onChange={(e) => setTotalCashback(e.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-wide text-gray-500">
                    Customer cashback (DKK)
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="mt-1 block w-[180px] p-2 border border-gray-300 rounded-md"
                    value={customerCashback}
                    onChange={(e) => setCustomerCashback(e.target.value)}
                  />
                </label>
              </div>
              {amountError && <div className="text-amber-700 text-sm mt-3">{amountError}</div>}
            </>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Total assigned cashback">
                {formatCurrency(request.totalAssignedCashback ?? 0)} DKK
              </Field>
              <Field label="Customer assigned cashback">
                {formatCurrency(request.customerAssignedCashback ?? 0)} DKK
              </Field>
            </div>
          )}
        </Section>

        {isPending && claimConfig && (
          <Section title={`Send claim to ${claimConfig.label}`}>
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="text"
                className="w-[260px] p-2 border border-gray-300 rounded-md"
                placeholder="Order ID"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
              />
              <Button onClick={submitClaim} disabled={claimSubmitting || !orderId.trim()}>
                {claimSubmitting ? "Sending..." : "Send Claim"}
              </Button>
            </div>
            {claimMessage && <div className="text-sm mt-2">{claimMessage}</div>}
          </Section>
        )}
      </div>

      <footer className="px-6 py-4 border-t border-gray-200">
        {error && (
          <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg mb-3">
            {error}
          </div>
        )}

        {pendingAction ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm">
              {pendingAction === "accept"
                ? `Accept this request and credit the customer ${customerCashback} DKK?`
                : "Decline this cashback request?"}
            </p>
            <div className="flex gap-3">
              <Button
                variant={pendingAction === "accept" ? "primary" : "danger"}
                onClick={runAction}
                disabled={submitting}
              >
                {submitting ? "Working..." : "Confirm"}
              </Button>
              <Button variant="outline" onClick={() => setPendingAction(null)} disabled={submitting}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex justify-between gap-3">
            <div className="flex gap-3">
              {isPending && (
                <>
                  <Button onClick={() => setPendingAction("accept")} disabled={amountError !== null}>
                    ✓ Accept
                  </Button>
                  <Button variant="danger" onClick={() => setPendingAction("decline")}>
                    ✗ Decline
                  </Button>
                </>
              )}
            </div>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        )}
      </footer>
    </Modal>
  );
};

export default CashbackRequestDetailsModal;
